import React, { useState } from "react";
import axios from "axios";
import { Layers, Eye, ShieldCheck } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal/ScrollReveal";
import { CONTACTS } from "@/constants/contacts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPARE_ROWS = [
  ["Padronização de prontuários", "Inconsistente, depende do profissional", "Padrão único, alinhado ao COFFITO"],
  ["Tempo de documentação", "30–60 min/profissional/dia", "5–10 min (revisão + aprovação)"],
  ["Auditoria interna", "Manual, custosa", "Centralizada, automática"],
  ["Onboarding de novos fisios", "Treinamento extenso", "Padrão de prontuário herdado"],
  ["Risco regulatório", "Alto e desconhecido", "Mitigado por arquitetura"],
];

const SEAT_TIERS = [
  ["3 assentos", "R$ 297/mês"],
  ["5 assentos", "R$ 495/mês"],
  ["10 assentos", "R$ 990/mês"],
];

const EMPTY_FORM = { name: "", clinic: "", professionals: "", email: "", phone: "", message: "", website: "" };

const FIELD_LABELS = {
  name: "Nome",
  clinic: "Clínica",
  professionals: "Número de profissionais",
  email: "E-mail",
  phone: "WhatsApp",
  message: "Mensagem",
};

const submitErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const custom = detail.find((d) => d.type === "value_error");
    if (custom) return custom.msg.replace(/^Value error, /, "");
    const fields = [...new Set(detail.map((d) => FIELD_LABELS[d.loc?.[1]]).filter(Boolean))];
    if (fields.length) return `Confira: ${fields.join(", ")}.`;
  }
  return "Não conseguimos enviar agora. Tente novamente em instantes.";
};

export default function ForClinicsPage() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, { ...form, source: "site-institucional" });
      setStatus("success");
      setForm(EMPTY_FORM);
    } catch (err) {
      setStatus("error");
      setErrorMsg(submitErrorMessage(err));
    }
  };

  return (
    <>
      <section className="page-intro" data-testid="page-intro">
        <div className="container">
          <ScrollReveal>
            <span className="eyebrow">PARA CLÍNICAS</span>
            <h1 className="h-1">Akali AI para clínicas e estúdios de Pilates</h1>
            <p className="body-lg page-intro-sub">
              Padronize a documentação de toda a equipe. Sem retrabalho, sem
              risco regulatório.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-surface" data-testid="section-value">
        <div className="container">
          <div className="value-grid">
            <ScrollReveal delay={0.0} className="value-card glass glass-hover" data-testid="value-card-1">
              <div className="step-icon">
                <Layers size={22} />
              </div>
              <h3 className="value-card-title">Padronização</h3>
              <p className="value-card-body">
                Mesma estrutura de prontuário para todos os profissionais — do
                fisio sênior ao recém-formado. Onboarding mais rápido, auditoria
                interna trivial.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.1} className="value-card glass glass-hover" data-testid="value-card-2">
              <div className="step-icon">
                <Eye size={22} />
              </div>
              <h3 className="value-card-title">Visibilidade</h3>
              <p className="value-card-body">
                Dashboard centralizado com produtividade de cada profissional,
                volume de atendimentos e taxa de aprovação de prontuários no
                primeiro envio.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="value-card glass glass-hover" data-testid="value-card-3">
              <div className="step-icon">
                <ShieldCheck size={22} />
              </div>
              <h3 className="value-card-title">Conformidade</h3>
              <p className="value-card-body">
                Auditoria centralizada, campos COFFITO por default em toda a
                clínica. Logs imutáveis para perícia e fiscalização.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="section section-deep" data-testid="section-clinic-compare">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">COMPARATIVO</span>
              <h2 className="h-2">Antes e depois do Akali AI</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="compare-table" data-testid="clinic-compare-table">
              <div className="compare-table-row compare-table-header">
                <div className="compare-table-cell">Dimensão</div>
                <div className="compare-table-cell compare-table-header-bad">
                  Clínica sem Akali AI
                </div>
                <div className="compare-table-cell compare-table-header-good">
                  Clínica com Akali AI
                </div>
              </div>
              {COMPARE_ROWS.map((row) => (
                <div key={row[0]} className="compare-table-row">
                  <div className="compare-table-cell">{row[0]}</div>
                  <div className="compare-table-cell">{row[1]}</div>
                  <div className="compare-table-cell">{row[2]}</div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-surface" data-testid="section-clinic-pricing">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">PLANOS CLÍNICA</span>
              <h2 className="h-2">R$ 99 por assento, sem complicação</h2>
              <p className="body" style={{ maxWidth: 540, margin: "16px auto 0" }}>
                Mínimo de 3 assentos. Cada assento inclui todos os recursos do
                plano Solo, mais o painel multi-usuário e relatórios de equipe.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="seat-table" data-testid="seat-table">
              <div className="seat-row seat-row-header">
                <span>Quantidade</span>
                <span style={{ textAlign: "right" }}>Mensal</span>
              </div>
              {SEAT_TIERS.map(([k, v]) => (
                <div className="seat-row" key={k}>
                  <span className="seat-row-label">{k}</span>
                  <span className="seat-row-value">{v}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section
        className="section section-deep"
        id="contato"
        data-testid="section-contact"
      >
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">CONTATO</span>
              <h2 className="h-2">Falar com a equipe sobre sua clínica</h2>
              <p className="body" style={{ maxWidth: 520, margin: "16px auto 0" }}>
                Conte sobre seu time. Voltamos em até 24h com uma proposta
                customizada.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="glass form-card" data-testid="contact-form-card">
              {status === "success" ? (
                <div className="form-success" data-testid="contact-form-success">
                  ✓ Recebemos sua mensagem. Entraremos em contato em até 24h.
                  <p
                    className="mono-sm"
                    style={{
                      marginTop: 16,
                      color: "var(--color-text-muted)",
                    }}
                  >
                    Urgente? Escreva direto para{" "}
                    <a
                      href={`mailto:${CONTACTS.general}`}
                      className="legal-mail"
                      data-testid="forclinics-contact-email"
                    >
                      {CONTACTS.general}
                    </a>
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form">
                  <div className="form-row-two">
                    <div className="form-row">
                      <label className="form-label" htmlFor="contact-name">Nome</label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        minLength={2}
                        maxLength={120}
                        className="form-input"
                        value={form.name}
                        onChange={onChange("name")}
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div className="form-row">
                      <label className="form-label" htmlFor="contact-clinic">Clínica</label>
                      <input
                        id="contact-clinic"
                        type="text"
                        required
                        minLength={2}
                        maxLength={160}
                        className="form-input"
                        value={form.clinic}
                        onChange={onChange("clinic")}
                        data-testid="contact-input-clinic"
                      />
                    </div>
                  </div>

                  <div className="form-row-two">
                    <div className="form-row">
                      <label className="form-label" htmlFor="contact-professionals">
                        Número de profissionais
                      </label>
                      <input
                        id="contact-professionals"
                        type="text"
                        required
                        maxLength={30}
                        className="form-input"
                        value={form.professionals}
                        onChange={onChange("professionals")}
                        data-testid="contact-input-professionals"
                      />
                    </div>
                    <div className="form-row">
                      <label className="form-label" htmlFor="contact-email">E-mail</label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        maxLength={254}
                        className="form-input"
                        value={form.email}
                        onChange={onChange("email")}
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <label className="form-label" htmlFor="contact-phone">
                      WhatsApp (opcional)
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      maxLength={30}
                      placeholder="(11) 98765-4321"
                      className="form-input"
                      value={form.phone}
                      onChange={onChange("phone")}
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div className="form-hp" aria-hidden="true">
                    <label htmlFor="contact-website">Site</label>
                    <input
                      id="contact-website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange("website")}
                      data-testid="contact-input-website"
                    />
                  </div>

                  <div className="form-row">
                    <label className="form-label" htmlFor="contact-message">
                      Mensagem (opcional)
                    </label>
                    <textarea
                      id="contact-message"
                      className="form-textarea"
                      maxLength={2000}
                      value={form.message}
                      onChange={onChange("message")}
                      data-testid="contact-input-message"
                    />
                  </div>

                  {errorMsg && (
                    <p className="form-error" data-testid="contact-form-error">
                      {errorMsg}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary btn-full"
                    disabled={status === "loading"}
                    data-testid="contact-submit"
                  >
                    {status === "loading" ? "Enviando..." : "Enviar mensagem"}
                  </button>
                  <p className="mono-xs form-privacy-note" data-testid="contact-privacy-note">
                    Usaremos seus dados apenas para responder ao seu contato.
                  </p>
                </form>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
}
